import { SyntheticEvent, useCallback, useState, type ReactNode } from "react";
import { Body, Link, SectionHeader } from "@sprout/typography";
import { Select, type Options, Option, OnSelectParam } from "@sprout/select";
import { PrimaryButton } from "@sprout/button";
import Button from "@sprout/button";
import useCsrfToken from "@hooks/use_csrf";
import CompanyLogo from "@components/company_logo";
import { useQueryClient } from "@tanstack/react-query";
import { router } from "@inertiajs/react";
import SearchIcon from "@/sprout/icons/search";
import { TextInput } from "@sprout/text_input.tsx";

import "./subscription.scss";
import useTranslation from "@hooks/use_translation";
import Container from "@components/ui/container.tsx";
import BackButton from "@components/back_button.tsx";
import KeywordTags from "@components/keyword_tags.tsx";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@components/ui/dialog.tsx";
import { Trans } from "react-i18next";
import ErrorIcon from "@sprout/icons/error.tsx";
import HelpPopover from "@sprout/help_popover.tsx";

type Method = "PUT" | "POST";

type Props = {
  departments: Options;
  selected: Options;
  pageTitle: string;
  subscriptionPath: string;
  subscriptionsPath: string;
  subscriptionId: number;
  method: Method;
  logoUrl?: string;
  companyName: string;
  allowDepartmentAlerts: boolean;
  allowKeywordAlerts: boolean;
  keywords: string[];
};

const ConfirmationModal = ({
  onConfirm,
  children,
}: {
  onConfirm: () => void;
  children: ReactNode;
}) => {
  return (
    <Dialog>
      <DialogTrigger>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="leading-[1.5rem]">
            <div className="flex gap-4">
              <ErrorIcon color="error-red" />
              <Trans i18nKey="jobAlerts:subscription:deleteModal:title" />
            </div>
          </DialogTitle>
        </DialogHeader>
        <DialogDescription>
          <Body>
            <Trans i18nKey="jobAlerts:subscription:deleteModal:description" />
          </Body>
        </DialogDescription>
        <DialogFooter>
          <DialogClose>
            <Button>
              <Trans i18nKey="jobAlerts:subscription:deleteModal:cancel" />
            </Button>
          </DialogClose>
          <DialogClose>
            <Button display="destructive" onClick={onConfirm}>
              <Trans i18nKey="jobAlerts:subscription:deleteModal:confirm" />
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Subscription = ({
  departments,
  method,
  selected,
  pageTitle,
  subscriptionPath,
  subscriptionsPath,
  subscriptionId,
  logoUrl,
  companyName,
  keywords,
  allowKeywordAlerts,
  allowDepartmentAlerts,
}: Props) => {
  const { t } = useTranslation("jobAlerts");
  const csrfToken = useCsrfToken();
  const [isDirty, setIsDirty] = useState(false);
  const [selectedDepartments, setSelectedDepartments] = useState<Options>(selected);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formKeywords, setFormKeywords] = useState<string[]>(keywords || []);
  const [newKeyword, setNewKeyword] = useState<string>("");
  const [keywordError, setKeywordError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const submitSubscriptionSettings = useCallback(async () => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);

    try {
      const response = await fetch(subscriptionPath, {
        method,
        body: JSON.stringify({
          id: subscriptionId,
          departments: selectedDepartments.map((department: Option) => department.value),
          keywords: formKeywords,
          authenticity_token: csrfToken,
        }),
        headers: { "Content-Type": "application/json" },
      });

      if (response.ok) {
        setIsDirty(false);
        await queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
        method === "POST" ? router.visit("/dashboard") : router.visit("/subscriptions");
        return;
      }

      const { message } = await response.json();

      if (!message) {
        throw new Error("Not ok");
      }

      setError(message);
    } catch (e) {
      console.error(e);
      setError(t("defaultError"));
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, selectedDepartments, queryClient, router, formKeywords]);

  const onSelect = useCallback((option: OnSelectParam) => {
    setSelectedDepartments(option as Options);
    setIsDirty(true);
  }, []);

  const handleDelete = useCallback(async () => {
    if (isSubmitting) {
      return;
    }

    try {
      const response = await fetch(subscriptionPath, {
        method: "DELETE",
        body: JSON.stringify({
          authenticity_token: csrfToken,
        }),
        headers: { "Content-Type": "application/json" },
      });

      if (response.ok) {
        await queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
        router.visit("/subscriptions");
        return;
      }

      setError(t("defaultError"));
      setKeywordError(t("defaultError"));
    } catch (e) {
      console.error(e);
      setError(t("defaultError"));
      setKeywordError(t("defaultError"));
    }
  }, [subscriptionPath, csrfToken, queryClient, t]);

  const addKeyword = (e: SyntheticEvent) => {
    e.preventDefault();
    if (formKeywords.length >= 8 && newKeyword.length) {
      setKeywordError(t("subscription.keywords.too_many"));
      return;
    }
    if (newKeyword.length) {
      setNewKeyword("");
      setFormKeywords((keywords) => keywords.concat([newKeyword]));
      setIsDirty(true);
    }
  };

  const removeKeyword = (label: string) => {
    setFormKeywords((keywords) => {
      const index = keywords.indexOf(label);
      if (index !== -1) {
        setIsDirty(true);
        setKeywordError(null);
        return [...keywords.slice(0, index), ...keywords.slice(index + 1)];
      }
      return keywords;
    });
  };

  return (
    <Container>
      <BackButton href={subscriptionsPath} />
      <CompanyLogo companyName={companyName} logoUrl={logoUrl} />
      <div className="flex flex-col gap-[4px]">
        <SectionHeader as="h1">{pageTitle}</SectionHeader>
        <Body>{t("subscription.selectJobs")}</Body>
      </div>
      <div className="subscription">
        {allowDepartmentAlerts && (
          <div className="subscription__input">
            <Select
              selected={selectedDepartments}
              short={true}
              id="select-departments"
              label={t("subscription.department")}
              onSelect={onSelect}
              options={departments}
              isMulti={true}
              isClearable={true}
              error={error}
            />
          </div>
        )}
        {allowKeywordAlerts && (
          <div className="subscription-keyword gap-[8px]">
            <div className="subscription-keyword__input-row gap-[12px]">
              <div className="subscription-keyword__input-mobile-row gap-[12px]">
                <div className="subscription-keyword__input">
                  <TextInput
                    id="keyword"
                    placeholder={t("subscription.keywords.placeholder")}
                    maxLength={255}
                    short={true}
                    onChange={(event) => setNewKeyword(event.currentTarget.value || "")}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        addKeyword(event);
                      }
                    }}
                    value={newKeyword}
                    narrow={true}
                    icon={<SearchIcon />}
                    error={keywordError}
                    help={t("subscription.keywords.subtitle")}
                  />
                </div>
                <HelpPopover text={t("subscription.keywords.helperText")} />
              </div>
              <Button
                onClick={addKeyword}
                className={"subscription subscription-keyword__button"}
                fitContent
                disabled={!!keywordError}
                size="medium"
              >
                {t("subscription.keywords.add")}
              </Button>
            </div>
            <KeywordTags keywords={formKeywords} onClick={removeKeyword} />
          </div>
        )}
        <div className="subscription__buttons">
          <PrimaryButton
            onClick={submitSubscriptionSettings}
            loading={isSubmitting}
            disabled={!isDirty || !(formKeywords.length || selectedDepartments.length)}
          >
            {method === "POST" ? t("subscription.create") : t("subscription.update")}
          </PrimaryButton>
          {method !== "POST" && (
            <ConfirmationModal onConfirm={handleDelete}>
              <Button display="danger">{t("subscription.delete")}</Button>
            </ConfirmationModal>
          )}
          <Link href={subscriptionsPath}>{"Cancel"}</Link>
        </div>
      </div>
    </Container>
  );
};

export default Subscription;
